import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { ChatListSidebarItem, ConversationHeader } from '../schema'
import { kNewConversationTitle } from '../constants'
import { kRouteSegments } from '@/routing/paths'
import { ChatOverflowMenu } from '@/chat-common/conversation_menu/ChatOverflowMenu'
import ConversationDeleteConfirmDialog from '@/chat-common/components/dialogs/ConversationDeleteConfirmDialog'
import { RootState } from '@/store/store'
import ChatTitleChangeDialog from '@/chat-common/components/dialogs/ChatTitleChangeDialog'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import clsx from 'clsx'
import { selectChatListSidebarItems } from '@/chat-common/conversation_list/selectors'
import {
  selectMatterConversationsError,
  selectMatterConversationsInitialLoadComplete,
  selectMatterConversationsLoading,
} from '@/agent/conversations/store/selectors'
import { AuthContext } from '@/context/auth-context'
import { List } from 'react-window'
import { useIsConversationOwner } from '@/agent/conversations/hooks/useIsConversationOwner'
import { Tag } from '@/components/tag/Tag'

type ChatListDateRange = {
  title: string
  conversations: ConversationHeader[]
}

// Row heights for react-window virtualization
const HEADER_ROW_HEIGHT = 42 // Calculated pt-4 (16px) + pb-0.5 (2px) + text height (~24px)
const CONVERSATION_ROW_HEIGHT = 32
const CONVERSATION_ROW_HEIGHT_WITH_PADDING = 36

type ChatListSidebarProps = {
  onClickCallback?: () => void
}

/**
 * Small "View Only" tag surfaced (on hover, matching the overflow menu) for sidebar chats the
 * current user does not own. Owners render nothing.
 */
function ChatListViewOnlyIndicator({ conversationId, isRowActive }: { conversationId: string; isRowActive: boolean }) {
  const isOwner = useIsConversationOwner(conversationId)
  if (isOwner) return null

  return (
    <div
      className={clsx(
        // Mobile: always visible and reserving space
        'flex-shrink-0 flex items-center px-1',
        // Desktop: takes no space until the row is hovered
        'lg:hidden lg:group-hover:flex',
        // Active row (open menu / selected): always shown on desktop
        isRowActive && 'lg:flex'
      )}
    >
      <Tag variant="tonal" color="Black" size="2xs">
        View Only
      </Tag>
    </div>
  )
}

export default function ChatListSidebar(props: ChatListSidebarProps) {
  const { trackEvent } = useAnalytics()
  const { chatFeature, chatId } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { onClickCallback } = props

  const authContext = useContext(AuthContext)
  const user = authContext?.user
  const userIsSignedIn = !!user && !user.isAnonymous

  const errorMessage = 'Error loading chat history'

  // Local state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteDialogItem, setDeleteDialogItem] = useState<ChatListSidebarItem | null>(null)
  const [changeTitleDialogOpen, setChangeTitleDialogOpen] = useState(false)
  const [changeTitleItem, setChangeTitleItem] = useState<ChatListSidebarItem | null>(null)
  const [menuOpenForId, setMenuOpenForId] = useState<string | null>(null)
  // Default height of 600px for virtualized list; actual height is updated via ResizeObserver
  const [containerHeight, setContainerHeight] = useState(600)
  const containerRef = useRef<HTMLDivElement>(null)

  // Validate the chat feature
  const validFeature: boolean = chatFeature === kRouteSegments.assistant

  // Get the active matter id to show the conversations based on the active matter
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const initialMatterLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)

  // Agent conversation state, scoped to the current matter
  const loading = useAppSelector((state: RootState) => selectMatterConversationsLoading(state, { matterId: activeMatterId }))
  const error = useAppSelector((state: RootState) => selectMatterConversationsError(state, { matterId: activeMatterId }))
  const initialLoadComplete = useAppSelector((state: RootState) => selectMatterConversationsInitialLoadComplete(state, { matterId: activeMatterId }))

  const allConversationsArray = useAppSelector((state: RootState) => selectChatListSidebarItems(state, { matterId: activeMatterId, currentUserId: user?.uid }))

  // Whether to show the error message
  const showError = (error || !validFeature) && !loading

  // Whether to show the loader
  const showLoader =
    !initialLoadComplete || // the matter's chat list initial load has not finished
    (loading && allConversationsArray.length == 0) || // the matter's conversations are loading and there are none to show yet
    (userIsSignedIn && !initialMatterLoadComplete) // if the user is signed in and the matter list has not finished loading (required since we filter conversations by matter)

  // Deletion functions
  // Open the confirm delete dialog
  const openDeleteDialog = useCallback((conversationItem: ChatListSidebarItem) => {
    setDeleteDialogItem(conversationItem)
    setDeleteDialogOpen(true)
  }, [])

  // Rename functions
  // Open the change title dialog
  const openChangeTitleDialog = useCallback((conversationItem: ChatListSidebarItem) => {
    setChangeTitleItem(conversationItem)
    setChangeTitleDialogOpen(true)
  }, [])

  const handleChatClick = useCallback(
    (id: string) => {
      // Navigate to the chat (requires matter context)
      if (!activeMatterId) return
      navigate(buildConversationPath(activeMatterId, id))
    },
    [navigate, activeMatterId]
  )

  // Fetch the current matter's chat history when this component renders or the matter changes (cached per matter at the listener level)
  useEffect(() => {
    dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId: activeMatterId, forceRefresh: false }))
  }, [activeMatterId, dispatch])

  // Track container height for react-window
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height)
      }
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [])

  // Build flattened list for virtualization
  type ListItem = { type: 'header'; title: string } | { type: 'conversation'; conversation: ConversationHeader; isNew: boolean }

  const flattenedItems = useMemo(() => {
    const items: ListItem[] = []

    // First, separate new and existing conversations
    const newConversations: ConversationHeader[] = allConversationsArray.filter((conversation) => conversation.title == kNewConversationTitle)
    const existingConversations: ConversationHeader[] = allConversationsArray.filter((conversation) => conversation.title != kNewConversationTitle)

    // Add new conversations first (reversed to maintain order)
    const reversedNew = [...newConversations].reverse()
    reversedNew.forEach((conversation) => {
      items.push({ type: 'conversation', conversation, isNew: true })
    })

    // Construct dates for the time intervals
    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const startOfYesterday = new Date(startOfToday)
    startOfYesterday.setDate(startOfYesterday.getDate() - 1)
    const sevenDaysAgo = new Date(startOfToday)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    // Construct empty lists for each time interval
    const todaysConversations: ConversationHeader[] = []
    const yesterdaysConversations: ConversationHeader[] = []
    const last7DaysConversations: ConversationHeader[] = []

    // Collect the date ranges into an iterable
    const dateSegmented: ChatListDateRange[] = [
      { title: 'Today', conversations: todaysConversations },
      { title: 'Yesterday', conversations: yesterdaysConversations },
      { title: 'Last 7 days', conversations: last7DaysConversations },
    ]

    // Categorize existing conversations by date
    for (const conversation of existingConversations) {
      const conversationDate = new Date(conversation.modified_on)

      if (conversationDate >= startOfToday) {
        todaysConversations.push(conversation)
      } else if (conversationDate >= startOfYesterday && conversationDate < startOfToday) {
        yesterdaysConversations.push(conversation)
      } else if (conversationDate >= sevenDaysAgo && conversationDate < startOfYesterday) {
        last7DaysConversations.push(conversation)
      } else {
        const month = conversationDate.toLocaleString('default', { month: 'short' })
        const year = conversationDate.getFullYear().toString()
        const title = `${month}, ${year}`

        const existingTitle = dateSegmented.find((range) => range.title == title)
        if (existingTitle) {
          existingTitle.conversations.push(conversation)
        } else {
          dateSegmented.push({ title: title, conversations: [conversation] })
        }
      }
    }

    // Flatten into items array with headers
    dateSegmented.forEach((range) => {
      if (range.conversations.length > 0) {
        items.push({ type: 'header', title: range.title })
        range.conversations.forEach((conversation) => {
          items.push({
            type: 'conversation',
            conversation,
            isNew: false,
          })
        })
      }
    })

    return items
  }, [allConversationsArray])

  const getRowHeight = useCallback(
    (index: number) => {
      const item = flattenedItems[index]
      if (item.type === 'header') return HEADER_ROW_HEIGHT
      return CONVERSATION_ROW_HEIGHT_WITH_PADDING
    },
    [flattenedItems]
  )

  // Ref for react-window v2 API
  const listRef = useRef<{
    element: HTMLDivElement | null
    scrollToRow: (config: { align?: 'auto' | 'center' | 'end' | 'smart' | 'start'; behavior?: 'auto' | 'instant' | 'smooth'; index: number }) => void
  }>(null)

  // Scroll to active conversation
  useEffect(() => {
    if (chatId && listRef.current) {
      const activeIndex = flattenedItems.findIndex((item) => item.type === 'conversation' && item.conversation.id === chatId)
      if (activeIndex >= 0) {
        setTimeout(() => {
          listRef?.current?.scrollToRow({ index: activeIndex, align: 'smart' })
        }, 0)
      }
    }
  }, [chatId, flattenedItems])

  // Row renderer for react-window v2 - receives index and style as props
  const RowComponent = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const item = flattenedItems[index]

      if (item.type === 'header') {
        return (
          <>
            <div style={style} className="block px-2 pb-0.5 pt-4 text-xs font-bold text-brand-neutral-700">
              {item.title}
            </div>
          </>
        )
      }

      const { conversation, isNew } = item
      const active = conversation.id === chatId
      const isMenuOpen = menuOpenForId == conversation.id
      const isActive = active || isMenuOpen

      return (
        <div style={style}>
          <div
            data-testid={`chat-list-sidebar-${conversation.id}`}
            title={conversation.title}
            style={{ height: CONVERSATION_ROW_HEIGHT }}
            className={clsx(
              'group flex justify-between items-center whitespace-nowrap pl-2 py-0.5 lg:py-2 pr-2 text-sm text-brand-neutral-800 cursor-pointer rounded-md hover:bg-brand-neutral-100',
              isActive ? 'bg-brand-neutral-100' : ''
            )}
            onClick={() => {
              if (isNew) {
                trackEvent(AnalyticsEvent.OpenExistingConversation)
              }
              handleChatClick(conversation.id)
              if (onClickCallback) onClickCallback()
            }}
          >
            <div className={`flex-1 overflow-hidden whitespace-nowrap chat-list-item-text-fade`}>{conversation.title}</div>

            <ChatListViewOnlyIndicator conversationId={conversation.id} isRowActive={isActive} />

            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <ChatOverflowMenu
                chatListSidebarItem={conversation}
                onSelectRename={() => openChangeTitleDialog(conversation)}
                onSelectDelete={() => openDeleteDialog(conversation)}
                open={menuOpenForId === conversation.id}
                onOpenChange={(open) => setMenuOpenForId(open ? conversation.id : null)}
                showTriggerOnHover
                isRowActive={isActive}
                iconColorClass="text-brand-neutral-600"
              />
            </div>
          </div>
        </div>
      )
    },
    [flattenedItems, chatId, trackEvent, handleChatClick, onClickCallback, openChangeTitleDialog, openDeleteDialog, menuOpenForId]
  )

  const totalItems = flattenedItems.length

  return (
    <>
      {/* Dialogs - Always render */}
      <ChatTitleChangeDialog
        chatItem={changeTitleItem}
        dialogVisible={changeTitleDialogOpen}
        onDialogClose={() => {
          setChangeTitleDialogOpen(false)
          setChangeTitleItem(null)
        }}
      />
      <ConversationDeleteConfirmDialog
        chatItem={deleteDialogItem}
        dialogVisible={deleteDialogOpen}
        onDialogClose={() => {
          setDeleteDialogOpen(false)
          setDeleteDialogItem(null)
        }}
      />

      {/* Main content: loader / error / chat list */}
      <div ref={containerRef} className="overflow-hidden pt-1 ml-2 lg:mr-0 z-10 h-full">
        {showLoader ? (
          <div className="p-2 flex w-full h-full justify-center items-center">
            <CircularProgressContinuousSized size={12} thickness={7} />
          </div>
        ) : showError ? (
          <div className="p-2 text-red-700 text-sm text-center">{errorMessage}</div>
        ) : (
          <>
            {allConversationsArray.length === 0 && totalItems === 0 && <div className="text-brand-neutral-500 text-sm">No chat history</div>}
            {totalItems > 0 && (
              <List<Record<string, never>>
                listRef={listRef}
                defaultHeight={containerHeight}
                rowCount={totalItems}
                rowHeight={getRowHeight}
                rowComponent={RowComponent}
                rowProps={{}}
                className="list-none m-0 p-0"
                style={{ height: containerHeight }}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
